import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle, PaperPlaneTilt, Sparkle } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const revenueBuckets = [
  "Under ₹1 Cr",
  "₹1 Cr – ₹5 Cr",
  "₹5 Cr – ₹25 Cr",
  "₹25 Cr – ₹100 Cr",
  "₹100 Cr+",
];

const industries = [
  "Manufacturing", "Export", "Wholesale", "Retail", "IT Services",
  "Marketing Agencies", "Logistics", "Healthcare", "Construction", "Other",
];

const initialForm = {
  company_name: "",
  owner_name: "",
  email: "",
  mobile: "",
  city: "",
  industry: "",
  annual_revenue: "",
  monthly_invoice_value: "",
  gst_number: "",
  website: "",
  message: "",
  consent: false,
};

export default function EarlyAccessForm() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.company_name.trim()) e.company_name = "Company name is required";
    if (!form.owner_name.trim()) e.owner_name = "Your name is required";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email";
    if (form.mobile.replace(/\D/g, "").length < 6) e.mobile = "Enter a valid mobile number";
    if (!form.city.trim()) e.city = "City is required";
    if (!form.industry) e.industry = "Please select an industry";
    if (!form.annual_revenue) e.annual_revenue = "Please select annual revenue";
    if (!form.monthly_invoice_value.trim()) e.monthly_invoice_value = "Please enter monthly invoice value";
    if (!form.consent) e.consent = "Please confirm to continue";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
      toast.success("You're on the Invoquity Early Access list.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="early-access" data-testid="early-access-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-3xl glass p-10 md:p-14 text-center"
            data-testid="early-access-success"
          >
            <div className="mx-auto grid place-items-center w-16 h-16 rounded-2xl bg-emerald-700 text-white shadow-glow-blue">
              <CheckCircle size={30} weight="duotone" />
            </div>
            <h3 className="mt-6 font-display text-3xl md:text-4xl font-semibold text-slate-900">Thank you.</h3>
            <p className="mt-3 text-slate-600 text-base md:text-lg">
              You&apos;re now on the <span className="font-semibold text-slate-900">Invoquity Early Access</span> list.
              Our team will contact you shortly.
            </p>
            <button
              onClick={() => { setForm(initialForm); setSubmitted(false); }}
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
              data-testid="early-access-reset-btn"
            >
              Submit another
            </button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="early-access" data-testid="early-access-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-emerald-700 mb-3 inline-flex items-center gap-2 justify-center">
            <Sparkle weight="fill" size={14} /> Early Access
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900">
            Request an invitation.
          </h2>
          <p className="mt-4 text-slate-600 text-lg leading-relaxed">
            Tell us about your business. Founding customers get priority onboarding and dedicated support.
          </p>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.55 }}
          data-testid="early-access-form"
          className="mt-12 rounded-3xl glass p-6 md:p-10"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Company name *" error={errors.company_name}>
              <Input data-testid="input-company-name" value={form.company_name} onChange={(e) => setField("company_name", e.target.value)} placeholder="Acme Manufacturing Pvt Ltd" className="h-12 rounded-xl" />
            </Field>
            <Field label="Business owner name *" error={errors.owner_name}>
              <Input data-testid="input-owner-name" value={form.owner_name} onChange={(e) => setField("owner_name", e.target.value)} placeholder="Priya Sharma" className="h-12 rounded-xl" />
            </Field>
            <Field label="Email *" error={errors.email}>
              <Input data-testid="input-email" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="you@company.com" className="h-12 rounded-xl" />
            </Field>
            <Field label="Mobile number *" error={errors.mobile}>
              <Input data-testid="input-mobile" value={form.mobile} onChange={(e) => setField("mobile", e.target.value)} placeholder="+91 98xxxxxx" className="h-12 rounded-xl" />
            </Field>
            <Field label="City *" error={errors.city}>
              <Input data-testid="input-city" value={form.city} onChange={(e) => setField("city", e.target.value)} placeholder="Bengaluru" className="h-12 rounded-xl" />
            </Field>
            <Field label="Industry *" error={errors.industry}>
              <Select value={form.industry} onValueChange={(v) => setField("industry", v)}>
                <SelectTrigger data-testid="select-industry" className="h-12 rounded-xl">
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent>
                  {industries.map((it) => <SelectItem key={it} value={it}>{it}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Annual revenue *" error={errors.annual_revenue}>
              <Select value={form.annual_revenue} onValueChange={(v) => setField("annual_revenue", v)}>
                <SelectTrigger data-testid="select-annual-revenue" className="h-12 rounded-xl">
                  <SelectValue placeholder="Select bucket" />
                </SelectTrigger>
                <SelectContent>
                  {revenueBuckets.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Monthly invoice value *" error={errors.monthly_invoice_value}>
              <Input data-testid="input-monthly-invoice-value" value={form.monthly_invoice_value} onChange={(e) => setField("monthly_invoice_value", e.target.value)} placeholder="e.g. ₹25 Lakhs" className="h-12 rounded-xl" />
            </Field>
            <Field label="GST number (optional)">
              <Input data-testid="input-gst-number" value={form.gst_number} onChange={(e) => setField("gst_number", e.target.value)} placeholder="29ABCDE1234F1Z5" className="h-12 rounded-xl" />
            </Field>
            <Field label="Website (optional)">
              <Input data-testid="input-website" value={form.website} onChange={(e) => setField("website", e.target.value)} placeholder="https://yourcompany.com" className="h-12 rounded-xl" />
            </Field>
            <div className="md:col-span-2">
              <Field label="Message">
                <Textarea data-testid="input-message" rows={4} value={form.message} onChange={(e) => setField("message", e.target.value)} placeholder="Tell us briefly about your business or what you're looking to solve." className="rounded-xl" />
              </Field>
            </div>
          </div>

          <div className="mt-6 flex items-start gap-3">
            <Checkbox id="consent" data-testid="checkbox-consent" checked={form.consent} onCheckedChange={(v) => setField("consent", !!v)} />
            <label htmlFor="consent" className="text-sm text-slate-600 leading-relaxed">
              I want early access to Invoquity. I agree to be contacted by the Invoquity team about the platform.
            </label>
          </div>
          {errors.consent && <p className="mt-2 text-xs text-rose-600" data-testid="error-consent">{errors.consent}</p>}

          <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <p className="text-xs text-slate-500 max-w-md">
              Your details are stored securely and only used to onboard you to Invoquity Early Access.
            </p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="early-access-submit-btn"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-700 to-emerald-800 px-7 py-3.5 text-sm font-semibold text-white shadow-sm hover:shadow-glow-blue hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <PaperPlaneTilt size={18} weight="duotone" />
              {submitting ? "Submitting…" : "Request Early Access"}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-700 uppercase tracking-widest">{label}</Label>
      <div className="mt-2">{children}</div>
      {error && <p className="mt-1.5 text-xs text-rose-600" data-testid={`error-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{error}</p>}
    </div>
  );
}
